import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import CancelIcon from '@mui/icons-material/Cancel';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { authService } from '../../services/authService';
import { b2bService, B2bAccessRequest } from '../../services/b2bService';
import { palette } from '../../theme/colors';

const statusConfig: Record<string, { color: 'success' | 'warning' | 'error'; icon: React.ReactElement; label: string }> = {
  pending: { color: 'warning', icon: <HourglassEmptyIcon fontSize="small" />, label: 'Pending' },
  approved: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Approved' },
  rejected: { color: 'error', icon: <CancelIcon fontSize="small" />, label: 'Rejected' },
};

export const B2BAccessRequestsPage: React.FC = () => {
  const navigate = useNavigate();
  const isAuthenticated = authService.isAuthenticated();

  const { data: requests, isLoading, error } = useQuery({
    queryKey: ['my-b2b-access-requests'],
    queryFn: () => b2bService.getMyAccessRequests(),
    enabled: isAuthenticated,
  });

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" gutterBottom>
            Login Required
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            You need to be logged in to view your B2B access requests.
          </Typography>
          <Button
            component={RouterLink}
            to={`/login?redirect=${encodeURIComponent('/account/b2b-access')}`}
            variant="contained"
          >
            Login
          </Button>
        </Paper>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
        <Alert severity="error">Failed to load your B2B access requests. Please try again later.</Alert>
      </Container>
    );
  }

  const pendingRequests = requests?.filter((r) => r.status === 'pending') || [];
  const approvedRequests = requests?.filter((r) => r.status === 'approved') || [];
  const rejectedRequests = requests?.filter((r) => r.status === 'rejected') || [];

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/account-settings')}
          sx={{ color: palette.textSecondary }}
        >
          Back to Account Settings
        </Button>
      </Box>

      <Paper sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: palette.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <BusinessIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              B2B Access
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage your B2B portal access requests
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {requests?.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <BusinessIcon sx={{ fontSize: 64, color: palette.textMuted, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No B2B Access Requests
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              You haven't requested access to any B2B portals yet. Visit an organization's B2B portal
              to request wholesale pricing access.
            </Typography>
          </Box>
        ) : (
          <>
            {/* Approved Access */}
            {approvedRequests.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: 'success.main' }} />
                  Active B2B Access ({approvedRequests.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {approvedRequests.map((request) => (
                    <AccessRequestCard key={request.id} request={request} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Pending Requests */}
            {pendingRequests.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HourglassEmptyIcon sx={{ color: 'warning.main' }} />
                  Pending Requests ({pendingRequests.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {pendingRequests.map((request) => (
                    <AccessRequestCard key={request.id} request={request} />
                  ))}
                </Box>
              </Box>
            )}

            {/* Rejected Requests */}
            {rejectedRequests.length > 0 && (
              <Box>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CancelIcon sx={{ color: 'error.main' }} />
                  Rejected Requests ({rejectedRequests.length})
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {rejectedRequests.map((request) => (
                    <AccessRequestCard key={request.id} request={request} />
                  ))}
                </Box>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Container>
  );
};

interface AccessRequestCardProps {
  request: B2bAccessRequest;
}

const AccessRequestCard: React.FC<AccessRequestCardProps> = ({ request }) => {
  const status = statusConfig[request.status];
  const orgSlug = request.organization?.b2bPortalSlug;

  return (
    <Card variant="outlined" sx={{ '&:hover': { transform: 'none' } }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {request.organization?.name || 'Unknown Organization'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Company: {request.companyName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Submitted: {new Date(request.createdAt).toLocaleDateString()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Chip
              icon={status.icon}
              label={status.label}
              color={status.color}
              size="small"
              variant="outlined"
            />
            {request.status === 'approved' && request.assignedTier && (
              <Chip
                label={request.assignedTier.name}
                size="small"
                sx={{ bgcolor: palette.primaryLight, color: palette.primary }}
              />
            )}
          </Box>
        </Box>

        {/* Review notes */}
        {request.reviewNotes && request.status !== 'pending' && (
          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
              {request.status === 'approved' ? 'Approval Notes:' : 'Rejection Reason:'}
            </Typography>
            <Typography variant="body2">{request.reviewNotes}</Typography>
          </Box>
        )}

        {/* Ban info */}
        {request.isBanned && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2">
              You are blocked from re-requesting access.
              {request.bannedUntil && ` You can request again after ${new Date(request.bannedUntil).toLocaleDateString()}.`}
            </Typography>
            {request.banReason && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Reason: {request.banReason}
              </Typography>
            )}
          </Alert>
        )}

        {/* Actions */}
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          {request.status === 'approved' && orgSlug && (
            <Button
              component={RouterLink}
              to={`/b2b/${orgSlug}/products`}
              size="small"
              variant="contained"
              endIcon={<ArrowForwardIcon />}
            >
              Go to Portal
            </Button>
          )}
          {request.status === 'rejected' && !request.isBanned && orgSlug && (
            <Button
              component={RouterLink}
              to={`/b2b/${orgSlug}/request-access`}
              size="small"
              variant="outlined"
            >
              Request Again
            </Button>
          )}
          {orgSlug && request.status !== 'approved' && (
            <Button
              component={RouterLink}
              to={`/b2b/${orgSlug}`}
              size="small"
              variant="text"
            >
              View Portal
            </Button>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};
